import classNames from 'classnames/bind';
import styles from '../Dashboard.module.scss';

import { Box, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import RenderCustomLegend from '~/components/pie-chart/RenderCustomLegend';
import { useResponsiveValue } from '~/hooks/useResponsiveValue';
import { IServiceStatus } from '~/utils/interface/dashboard';

const cx = classNames.bind(styles);

const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, value, name, fill, responsiveLabel, index }: any) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * (midAngle ?? 1));
  const cos = Math.cos(-RADIAN * (midAngle ?? 1));

  const sx = cx + (outerRadius + 4) * cos;
  const sy = cy + (outerRadius + 4) * sin;

  const HORIZONTAL_OFFSET = responsiveLabel?.ex ?? 70;
  const VERTICAL_SPACING = 25;

  let ey = cy + (outerRadius + 20) * sin;

  if (sin >= 0) {
    ey = cy + (outerRadius + 20) * sin + index * VERTICAL_SPACING;
  } else {
    if (index === 0) {
      ey = cy + (outerRadius + 15) * sin - VERTICAL_SPACING;
    } else if (index === 1) {
      ey = cy + (outerRadius + 15) * sin;
    } else if (index === 2) {
      ey = cy + (outerRadius + 5) * sin - VERTICAL_SPACING * 2.5;
    }
  }

  const mx = cx + (outerRadius + 20) * cos;
  const my = ey;

  const ex = mx + (cos >= 0 ? 1 : -1) * HORIZONTAL_OFFSET;

  const textAnchor = cos >= 0 ? 'start' : 'end';

  const valueDy = 18;
  const FONT_HEIGHT_ADJUSTMENT = 5;

  const textX = mx + (cos >= 0 ? 5 : -5);
  const pathY = ey;

  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill='none' />
      <text
        x={textX}
        y={pathY - FONT_HEIGHT_ADJUSTMENT}
        textAnchor={textAnchor}
        fill='#333'
        fontSize={responsiveLabel?.fontSize}
      >
        {`${name}`}
      </text>
      <text
        x={textX}
        y={pathY - FONT_HEIGHT_ADJUSTMENT + valueDy}
        textAnchor={textAnchor}
        fill={fill}
        fontWeight={700}
        fontSize={responsiveLabel?.fontSize}
      >
        {value}
      </text>
    </g>
  );
};
const ServiceStatus = (props: { serviceStatus?: IServiceStatus }) => {
  const { t } = useTranslation('admin/home');
  const [data, setData] = useState<{ name: string; value: number; color: string; roundedValue: number }[]>([]);

  useEffect(() => {
    if (props.serviceStatus) {
      const data = [];
      if (props.serviceStatus.device_valid)
        data.push({
          name: 'Còn hạn',
          value: props.serviceStatus.device_valid,
          color: '#3D9149',
          roundedValue: 0,
        });

      if (props.serviceStatus.device_due)
        data.push({
          name: 'Đến hạn',
          value: props.serviceStatus.device_due,
          color: '#FF9500',
          roundedValue: 0,
        });

      if (props.serviceStatus.device_overdue)
        data.push({
          name: 'Quá hạn',
          value: props.serviceStatus.device_overdue,
          color: '#ED1002',
          roundedValue: 0,
        });

      const totalValue: number = data.reduce((sum, item) => sum + item.value, 0);
      let currentTotal = 0;
      for (let index = 0; index < data.length; index++) {
        if (index === data.length - 1) {
          data[index].roundedValue = 100 - currentTotal;
          break;
        }
        data[index].roundedValue = Math.round((data[index].value * 100) / totalValue);
        currentTotal += data[index].roundedValue;
      }

      setData(data);
    }
  }, [props.serviceStatus]);

  const responsiveFontSize = useResponsiveValue({
    lg: '10px',
    xl: '14px',
  });

  const responsiveLabel = useResponsiveValue({
    lg: {
      fontSize: '10px',
      textPosition: 15,
      ex: 40,
      outerRadius: '50%',
    },
    xl: {
      fontSize: '12px',
      textPosition: 35,
      ex: 70,
      outerRadius: '60%',
    },
  });

  return (
    <Box className={cx('2xl:w-[25%] lg:w-[29%] h-full', 'dashboard-widget')}>
      <Typography className={cx('dashboard-widget-title', 'dashboard-widget-title-text')}>
        {t('serviceStattus')}
      </Typography>
      <Box className={cx('dashboard-widget-body')}>
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Pie
              data={data}
              dataKey='value'
              nameKey='name'
              cx='50%'
              cy='50%'
              outerRadius={responsiveLabel?.outerRadius ?? '60%'}
              startAngle={90}
              endAngle={-270}
              fill='#82ca9d'
              labelLine={false}
              label={({ index, ...prev }) =>
                renderCustomLabel({
                  ...prev,
                  index,
                  responsiveLabel,
                })
              }
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <RenderCustomLegend fontSize={responsiveFontSize ?? '10px'} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default memo(ServiceStatus);
